import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Truck, Phone, Mail, MapPin, CheckCircle2, Star, Shield, ArrowRight, 
  FileCheck, HardHat, Globe, Heart, Menu, X, Zap, Eye, Users, Target, Send, Loader2
} from 'lucide-react';

/* ── Brand Palette ── */
const C = {
  gold: '#D4AF37',
  goldDark: '#8B6D1E',
  goldLight: '#F9E27E',
  cyan: '#00FFFF',
  navy: '#0D1B2A',
  cobalt: '#002D5A',
  anthracite: '#1A1A1A',
  black: '#050505',
};

const WHATSAPP = '+55 24 99938-4429';
const EMAIL = 'contato@intercontinentalbrasil.com.br';

/* ── Zod Form Schema ── */
const formSchema = z.object({
  nome: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres'),
  empresa: z.string().optional(),
  email: z.string().email('Email inválido'),
  telefone: z.string().min(10, 'Telefone inválido').regex(/[\d\s\-()]+/, 'Apenas números'),
  servico: z.string().min(1, 'Selecione um serviço'),
  mensagem: z.string().min(10, 'Mensagem deve ter pelo menos 10 caracteres'),
});
type FormData = z.infer<typeof formSchema>;

/* ── Framer Motion Variants ── */
const fadeUp = { hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } } };
const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };
const scaleIn = { hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: 'easeOut' } } };

/* ── Animated Section Wrapper ── */
function Section({ children, className, id, ariaLabel, bg }: { children: React.ReactNode; className?: string; id?: string; ariaLabel?: string; bg?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section ref={ref} id={id} className={className} aria-label={ariaLabel} style={bg ? { background: bg } : undefined}
      initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={stagger}>
      {children}
    </motion.section>
  );
}

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const go = (id: string) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onSubmit = async (data: FormData) => {
    await new Promise(r => setTimeout(r, 1200));
    setSubmitted(true);
    reset();
    setTimeout(() => setSubmitted(false), 4000);
  };

  const navLinks = [
    { label: 'Serviços', id: 'servicos' },
    { label: 'Projetos', id: 'projetos' },
    { label: 'Como Trabalhamos', id: 'processo' },
    { label: 'Sobre Nós', id: 'sobre' },
  ];

  return (
    <div className="site">
      {/* ═══ TOP BAR ═══ */}
      <div className="topbar">
        Atuação Nacional | B2B + Setor Público
      </div>

      {/* ═══ NAVBAR ═══ */}
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`} role="navigation" aria-label="Navegação principal">
        <div className="navbar__brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} role="button" aria-label="Voltar ao topo">
          <img src="/logo-sigma.png" alt="Logo" className="navbar__logo" />
          <div>
            <div className="navbar__title">INTERCONTINENTAL</div>
            <div className="navbar__subtitle">DO BRASIL LTDA</div>
          </div>
        </div>

        <button className="navbar__toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Abrir menu" aria-expanded={menuOpen}>
          {menuOpen ? <X size={28} color="white" /> : <Menu size={28} color="white" />}
        </button>

        <div className={`navbar__links ${menuOpen ? 'navbar__links--open' : ''}`}>
          {navLinks.map(l => (
            <button key={l.id} className="navbar__link" onClick={() => go(l.id)}>{l.label}</button>
          ))}
          <button className="btn btn--gold" onClick={() => go('contato')}>Solicitar Orçamento</button>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      {/* @ts-ignore */}
      <section className="hero" aria-label="Seção principal">
        <div className="hero__overlay" />
        <div className="hero__content">
          <div className="hero__text">
            <div className="hero__badge">
              <Globe size={16} color={C.cyan} />
              <span>ATUAÇÃO NACIONAL | B2B + SETOR PÚBLICO</span>
            </div>
            <h1 className="hero__h1">
              SOLUÇÕES<br/>
              <span className="text-gold">ESTRATÉGICAS</span><br/>
              EM CONSTRUÇÃO
            </h1>
            <p className="hero__desc">
              Fornecimento B2B completo para Steel Frame, execução de obras e participação em licitações públicas. Estrutura, tecnologia e confiança.
            </p>
            <div className="hero__buttons">
              <button className="btn btn--gold btn--lg" onClick={() => go('contato')}>
                Solicite Orçamento <ArrowRight size={20} />
              </button>
              <button className="btn btn--outline btn--lg" onClick={() => go('projetos')}>
                Ver Projetos
              </button>
            </div>
            <div className="hero__stats">
              {[
                { val: '100%', label: 'Regularidade Fiscal' },
                { val: '500+', label: 'Projetos Entregues' },
                { val: 'Nacional', label: 'Steel Frame' }
              ].map((s, i) => (
                <div key={i} className="hero__stat">
                  <div className="hero__stat-val">{s.val}</div>
                  <div className="hero__stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="hero__image">
            <motion.img src="/logo-sigma.png" alt="Sigma Protocol" className="hero__logo-float" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.5, ease: 'easeOut' }} />
          </div>
        </div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <div className="stats-bar">
        {[
          { n: '15+', l: 'Anos de Experiência' },
          { n: '800+', l: 'Projetos Entregues' },
          { n: '250+', l: 'Clientes Ativos' },
          { n: '26', l: 'Estados Atendidos' }
        ].map((s, i) => (
          <div key={i} className="stats-bar__item">
            <div className="stats-bar__num">{s.n}</div>
            <div className="stats-bar__label">{s.l}</div>
          </div>
        ))}
      </div>

      {/* ═══ SERVICES ═══ */}
      <Section id="servicos" className="services" ariaLabel="Nossos serviços">
        <div className="services__overlay" />
        <div className="container services__inner">
          <div className="section-header">
            <span className="section-header__tag">Nossos Serviços</span>
            <h2 className="section-header__title">TRÊS PILARES ESTRATÉGICOS</h2>
            <p className="section-header__desc">
              Atuação integrada em toda a cadeia da construção civil, desde o fornecimento de materiais até a execução completa de obras.
            </p>
          </div>
          <div className="services__grid">
            {[
              { title: 'Construção Civil', desc: 'Execução completa de obras com foco em estruturas metálicas e sistema Steel Frame de alta performance.', items: ['Execução de obras', 'Estruturas metálicas', 'Sistema Steel Frame', 'Reformas e ampliações', 'Obras institucionais'], icon: HardHat },
              { title: 'Fornecimento B2B', desc: 'Fornecimento completo de materiais para sistema Steel Frame em escala para construtoras e incorporadoras.', items: ['Perfis metálicos', 'Montantes e soleiras', 'Placas internas e externas', 'Componentes estruturais', 'Venda em escala'], icon: Truck },
              { title: 'Licitações Públicas', desc: 'Participação ativa em processos licitatórios com estrutura documental completa e atendimento multissetorial.', items: ['Participação em licitações', 'Fornecimento conforme edital', 'Atuação multissetorial', 'Prefeituras e estados', 'Órgãos federais'], icon: FileCheck }
            ].map((s, i) => (
              <motion.div key={i} className="service-card" variants={fadeUp} whileHover={{ y: -8, borderColor: C.gold }}>
                <div className="service-card__icon"><s.icon size={32} color="black" /></div>
                <h3 className="service-card__title">{s.title}</h3>
                <p className="service-card__desc">{s.desc}</p>
                <div className="service-card__list">
                  {s.items.map((item, j) => (
                    <div key={j} className="service-card__item">
                      <CheckCircle2 size={16} color={C.cyan} /> <span>{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ═══ PORTFOLIO ═══ */}
      <Section id="projetos" className="portfolio" ariaLabel="Portfólio de projetos">
        <motion.div className="container" variants={fadeUp}>
          <div className="section-header section-header--left">
            <span className="section-header__tag">Portfólio</span>
            <h2 className="section-header__title">PROJETOS REALIZADOS</h2>
          </div>
          <div className="portfolio__grid">
            {[
              { img: '/p1-edificio.png', cat: 'Construção Civil', title: 'Edifício Corporativo', desc: 'Estrutura metálica completa para edifício comercial de 12 andares.' },
              { img: '/p2-condominio.png', cat: 'Steel Frame', title: 'Condomínio Residencial', desc: '48 unidades habitacionais com estrutura Steel Frame.' },
              { img: '/p3-centro.png', cat: 'Licitação Pública', title: 'Centro Administrativo', desc: 'Obra pública municipal com fornecimento e montagem.' },
              { img: '/p4-galpao.png', cat: 'Fornecimento B2B', title: 'Galpão Industrial', desc: 'Fornecimento de estrutura metálica para galpão logístico.' },
              { img: '/p5-escola.png', cat: 'Licitação Pública', title: 'Escola Municipal', desc: 'Construção de unidade escolar com estrutura Steel Frame.' },
              { img: '/p6-hospital.png', cat: 'Construção Civil', title: 'Hospital Regional', desc: 'Ampliação de unidade hospitalar com estrutura metálica.' }
            ].map((p, i) => (
              <motion.div key={i} className="project-card" variants={scaleIn} whileHover={{ y: -6 }}>
                <div className="project-card__img">
                  <img src={p.img} alt={p.title} />
                </div>
                <div className="project-card__body">
                  <div className="project-card__cat">{p.cat}</div>
                  <h3 className="project-card__title">{p.title}</h3>
                  <p className="project-card__desc">{p.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* ═══ PROCESS ═══ */}
      <Section id="processo" className="process" ariaLabel="Processo de trabalho">
        <motion.div className="container process__inner" variants={fadeUp}>
          <div className="process__text">
            <span className="section-header__tag">Como Trabalhamos</span>
            <h2 className="section-header__title">PROCESSO ESTRUTURADO</h2>
            <div className="process__steps">
              {[
                { n: '01', title: 'Diagnóstico e Consultoria', desc: 'Análise completa das necessidades do projeto, avaliação técnica e proposta personalizada.' },
                { n: '02', title: 'Planejamento e Documentação', desc: 'Elaboração de cronograma, documentação técnica e regularização fiscal para licitações.' },
                { n: '03', title: 'Fornecimento e Logística', desc: 'Produção, armazenamento e entrega de materiais com rastreamento em tempo real.' },
                { n: '04', title: 'Execução e Entrega', desc: 'Montagem profissional, controle de qualidade e entrega dentro do prazo.' }
              ].map((item, i) => (
                <div key={i} className="process__step">
                  <div className="process__step-num">{item.n}</div>
                  <div>
                    <h4 className="process__step-title">{item.title}</h4>
                    <p className="process__step-desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="process__visual">
            <img src="/bg-noc.png" alt="Processo" className="process__img" />
            <div className="process__badge">
              <div className="process__badge-title">HUMANOS + IA</div>
              <div className="process__badge-sub">ENTREGA APRIMORADA</div>
            </div>
          </div>
        </motion.div>
      </Section>

      {/* ═══ DIFFERENTIALS ═══ */}
      <Section className="differentials" ariaLabel="Diferenciais">
        <motion.div className="container" variants={fadeUp}>
          <div className="section-header">
            <span className="section-header__tag">Diferenciais</span>
            <h2 className="section-header__title">POR QUE NOS ESCOLHER</h2>
          </div>
          <div className="differentials__grid">
            {[
              { icon: FileCheck, title: 'Estrutura Documental', desc: 'Documentação completa e organizada para participação em licitações públicas.' },
              { icon: Shield, title: 'Regularidade Fiscal', desc: '100% de regularidade fiscal e trabalhista, garantindo segurança nas operações.' },
              { icon: Truck, title: 'Logística Nacional', desc: 'Estrutura logística organizada para entregas em todos os estados do Brasil.' },
              { icon: Users, title: 'Atendimento Consultivo', desc: 'Equipe técnica especializada para suporte em todas as etapas do projeto.' },
              { icon: Zap, title: 'Fornecimento Completo', desc: 'Todos os componentes para Steel Frame em um único fornecedor.' },
              { icon: Target, title: 'Segurança Jurídica', desc: 'Contratos transparentes e segurança jurídica em todas as operações.' }
            ].map((d, i) => (
              <motion.div key={i} className="diff-card" variants={fadeUp} whileHover={{ y: -5 }}>
                <div className="diff-card__icon"><d.icon size={28} color={C.cyan} /></div>
                <h4 className="diff-card__title">{d.title}</h4>
                <p className="diff-card__desc">{d.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* ═══ TESTIMONIALS ═══ */}
      <Section className="testimonials" ariaLabel="Depoimentos de clientes">
        <motion.div className="container" variants={fadeUp}>
          <div className="section-header">
            <span className="section-header__tag">Depoimentos</span>
            <h2 className="section-header__title">O QUE DIZEM SOBRE NÓS</h2>
          </div>
          <div className="testimonials__grid">
            {[
              { name: 'Roberto Campos', role: 'Diretor - Construtora Horizonte', text: 'A Intercontinental entregou nosso projeto de edifício corporativo antes do prazo, com qualidade impecável. Recomendo fortemente para obras de grande porte.' },
              { name: 'Marina Santos', role: 'Engenheira Civil - Incorporadora Vista', text: 'O fornecimento de materiais para Steel Frame foi essencial para nosso condomínio. Qualidade dos perfis e atendimento técnico de primeira linha.' },
              { name: 'José Oliveira', role: 'Secretário de Obras - Prefeitura Municipal', text: 'Parceria estratégica em nossas licitações. A documentação sempre em dia e o cumprimento dos contratos nos dá segurança para novas obras.' }
            ].map((t, i) => (
              <div key={i} className="testi-card">
                <div className="testi-card__stars">
                  {[1,2,3,4,5].map(s => <Star key={s} size={18} fill={C.gold} color={C.gold} />)}
                </div>
                <p className="testi-card__text">"{t.text}"</p>
                <div className="testi-card__author">
                  <div className="testi-card__avatar">{t.name[0]}</div>
                  <div>
                    <div className="testi-card__name">{t.name}</div>
                    <div className="testi-card__role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* ═══ ABOUT / MISSION ═══ */}
      <Section id="sobre" className="about" ariaLabel="Sobre a empresa">
        <motion.div className="container about__inner" variants={fadeUp}>
          <div className="about__text">
            <span className="section-header__tag">Sobre Nós</span>
            <h2 className="section-header__title">MISSÃO, VISÃO E VALORES</h2>
            
            <div className="about__block">
              <div className="about__block-icon"><Eye size={28} color={C.gold} /></div>
              <div>
                <h3 className="about__block-title">Missão</h3>
                <p className="about__block-desc">Oferecer soluções completas em construção civil e fornecimento estratégico, atendendo o mercado privado e o setor público com eficiência, responsabilidade e segurança.</p>
              </div>
            </div>

            <div className="about__block">
              <div className="about__block-icon"><Target size={28} color={C.gold} /></div>
              <div>
                <h3 className="about__block-title">Visão</h3>
                <p className="about__block-desc">Ser referência nacional em fornecimento para Steel Frame, construção civil e participação em licitações públicas.</p>
              </div>
            </div>

            <div className="about__block">
              <div className="about__block-icon"><Shield size={28} color={C.gold} /></div>
              <div>
                <h3 className="about__block-title">Nossos Valores</h3>
                <div className="about__values">
                  {['Transparência', 'Compromisso', 'Responsabilidade', 'Ética', 'Eficiência', 'Qualidade'].map(v => (
                    <div key={v} className="about__value-item">
                      <CheckCircle2 size={16} color={C.cyan} /> {v}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="about__visual">
            <div className="about__img-wrapper">
              <img src="/bg-steel.png" alt="Construção Steel Frame" className="about__img" />
              <div className="about__img-overlay">
                <img src="/logo-sigma.png" alt="Intercontinental" className="about__img-logo" />
              </div>
            </div>
            <div className="about__badges">
              <div className="about__badge-item">
                <div className="about__badge-val">HUMANOS + IA</div>
                <div className="about__badge-label">ENTREGA INTELIGENTE</div>
              </div>
              <div className="about__badge-item">
                <div className="about__badge-val">CRA</div>
                <div className="about__badge-label">REGISTRADO</div>
              </div>
            </div>
          </div>
        </motion.div>
      </Section>

      {/* ═══ CONTACT ═══ */}
      <Section id="contato" className="contact" ariaLabel="Formulário de contato">
        <motion.div className="container contact__inner" variants={fadeUp}>
          <div className="contact__info">
            <h2 className="contact__title">VAMOS CONSTRUIR <span className="text-gold">JUNTOS</span></h2>
            <p className="contact__desc">Entre em contato para solicitar um orçamento ou tirar dúvidas. Nossa equipe está pronta para atender você.</p>
            <div className="contact__channels">
              <div className="contact__channel">
                <div className="contact__channel-icon"><Phone size={28} color={C.gold} /></div>
                <div>
                  <div className="contact__channel-label">CONTATO DIRETO</div>
                  <div className="contact__channel-value">{WHATSAPP}</div>
                </div>
              </div>
              <div className="contact__channel">
                <div className="contact__channel-icon"><Mail size={28} color={C.gold} /></div>
                <div>
                  <div className="contact__channel-label">AUDITORIA TÉCNICA</div>
                  <div className="contact__channel-value contact__channel-value--sm">{EMAIL}</div>
                </div>
              </div>
              <div className="contact__channel">
                <div className="contact__channel-icon"><MapPin size={28} color={C.gold} /></div>
                <div>
                  <div className="contact__channel-label">ABRANGÊNCIA</div>
                  <div className="contact__channel-value">Nacional — Todos os Estados</div>
                </div>
              </div>
            </div>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="contact__form" noValidate>
            <AnimatePresence>
              {submitted && (
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="form-success">
                  <CheckCircle2 size={24} color="#22c55e" /> Mensagem enviada com sucesso! Nossa equipe entrará em contato.
                </motion.div>
              )}
            </AnimatePresence>
            <div className="contact__form-row">
              <div className="form-group">
                <label>NOME COMPLETO</label>
                <input {...register('nome')} placeholder="Seu nome" className={errors.nome ? 'input-error' : ''} />
                {errors.nome && <span className="form-error">{errors.nome.message}</span>}
              </div>
              <div className="form-group">
                <label>EMPRESA</label>
                <input {...register('empresa')} placeholder="Nome da empresa" />
              </div>
            </div>
            <div className="contact__form-row">
              <div className="form-group">
                <label>EMAIL</label>
                <input {...register('email')} type="email" placeholder="seu@email.com" className={errors.email ? 'input-error' : ''} />
                {errors.email && <span className="form-error">{errors.email.message}</span>}
              </div>
              <div className="form-group">
                <label>TELEFONE</label>
                <input {...register('telefone')} placeholder="(00) 00000-0000" className={errors.telefone ? 'input-error' : ''} />
                {errors.telefone && <span className="form-error">{errors.telefone.message}</span>}
              </div>
            </div>
            <div className="form-group">
              <label>SERVIÇO DE INTERESSE</label>
              <select {...register('servico')} className={errors.servico ? 'input-error' : ''}>
                <option value="">Selecione um serviço</option>
                <option value="civil">Construção Civil</option>
                <option value="b2b">Fornecimento B2B</option>
                <option value="licitacao">Licitações Públicas</option>
              </select>
              {errors.servico && <span className="form-error">{errors.servico.message}</span>}
            </div>
            <div className="form-group">
              <label>MENSAGEM</label>
              <textarea {...register('mensagem')} rows={4} placeholder="Descreva seu projeto ou dúvida..." className={errors.mensagem ? 'input-error' : ''}></textarea>
              {errors.mensagem && <span className="form-error">{errors.mensagem.message}</span>}
            </div>
            <motion.button type="submit" className="btn btn--gold btn--full" disabled={isSubmitting} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              {isSubmitting ? <><Loader2 size={20} className="spin" /> ENVIANDO...</> : <><Send size={20} /> ENVIAR MENSAGEM</>}
            </motion.button>
          </form>
        </motion.div>
      </Section>

      {/* ═══ FOOTER ═══ */}
      <footer className="footer">
        <div className="container">
          <div className="footer__grid">
            <div className="footer__brand">
              <div className="footer__brand-row">
                <img src="/logo-sigma.png" alt="Logo" className="footer__logo" />
                <div>
                  <div className="footer__brand-name">INTERCONTINENTAL</div>
                  <div className="footer__brand-sub">DO BRASIL LTDA</div>
                </div>
              </div>
              <p className="footer__brand-desc">Soluções estratégicas em materiais para construção civil e atendimento ao setor público. Atuação nacional com foco em Steel Frame e licitações.</p>
            </div>
            <div className="footer__col">
              <h4 className="footer__col-title">LINKS RÁPIDOS</h4>
              {navLinks.map(l => <a key={l.id} href="#" onClick={(e) => { e.preventDefault(); go(l.id); }} className="footer__link">{l.label}</a>)}
            </div>
            <div className="footer__col">
              <h4 className="footer__col-title">CONTATO</h4>
              <p className="footer__link">{EMAIL}</p>
              <div style={{ marginTop: '1.5rem' }}>
                <div className="footer__col-title" style={{ marginBottom: '0.5rem' }}>CONTATO DIRETO</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 900 }}>{WHATSAPP}</div>
              </div>
            </div>
          </div>
          <div className="footer__bottom">
            <div className="footer__copy">© 2026 Intercontinental do Brasil Ltda. Todos os direitos reservados.</div>
            <div className="footer__credits">
              feito com <Heart size={16} color="#FF4B4B" fill="#FF4B4B" /> por <a href="https://crieOfertas.com.br" target="_blank" rel="noopener noreferrer">@crieOfertas.com.br</a>
            </div>
            <div className="footer__legal">
              <a href="#">Política de Privacidade</a>
              <a href="#">Termos de Uso</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ═══ FULL RESPONSIVE CSS ═══ */}
      <style>{`
        /* ── Reset & Base ── */
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; scroll-padding-top: 100px; }
        body { margin: 0; font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif; }
        ::selection { background: ${C.gold}; color: black; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: ${C.black}; }
        ::-webkit-scrollbar-thumb { background: #333; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: ${C.gold}; }
        img { max-width: 100%; height: auto; display: block; }
        
        .site { background: ${C.black}; color: white; min-height: 100vh; overflow-x: hidden; }
        .container { max-width: 1400px; margin: 0 auto; padding: 0 1.5rem; width: 100%; }
        .text-gold { color: ${C.gold}; }

        /* ── Buttons ── */
        .btn { border: none; cursor: pointer; font-weight: 800; border-radius: 50px; transition: all 0.3s; display: inline-flex; align-items: center; gap: 0.7rem; text-transform: uppercase; letter-spacing: 0.08em; font-size: 0.85rem; padding: 0.9rem 1.8rem; }
        .btn--gold { background: ${C.gold}; color: black; box-shadow: 0 8px 25px ${C.gold}44; }
        .btn--gold:hover { transform: translateY(-2px); box-shadow: 0 12px 35px ${C.gold}66; }
        .btn--outline { background: transparent; color: white; border: 1px solid rgba(255,255,255,0.25); backdrop-filter: blur(10px); }
        .btn--lg { padding: 1.2rem 2.4rem; font-size: 1rem; border-radius: 16px; }
        .btn--full { width: 100%; justify-content: center; padding: 1.4rem; font-size: 1.1rem; border-radius: 16px; }

        /* ── Section Headers ── */
        .section-header { text-align: center; margin-bottom: 4rem; }
        .section-header--left { text-align: left; }
        .section-header__tag { color: ${C.gold}; font-weight: 900; letter-spacing: 0.4em; text-transform: uppercase; font-size: 0.85rem; }
        .section-header__title { font-size: clamp(2rem, 5vw, 4rem); font-weight: 950; margin-top: 1rem; line-height: 1.1; }
        .section-header__desc { max-width: 700px; margin: 1.5rem auto 0; font-size: 1.1rem; color: rgba(255,255,255,0.6); line-height: 1.7; }

        /* ── TOP BAR ── */
        .topbar { background: linear-gradient(90deg, ${C.goldDark}, ${C.gold}, ${C.goldDark}); color: black; text-align: center; padding: 0.5rem 1rem; font-size: 0.75rem; font-weight: 900; letter-spacing: 0.15em; text-transform: uppercase; position: sticky; top: 0; z-index: 3000; }

        /* ── NAVBAR ── */
        .navbar { position: fixed; top: 2.5rem; left: 50%; transform: translateX(-50%); width: 94%; max-width: 1400px; background: rgba(13,27,42,0.5); backdrop-filter: blur(30px); border: 1px solid rgba(212,175,55,0.1); border-radius: 100px; padding: 0.6rem 1.5rem; display: flex; justify-content: space-between; align-items: center; z-index: 2000; transition: all 0.4s; }
        .navbar--scrolled { background: rgba(5,5,5,0.97); border-color: ${C.gold}44; }
        .navbar__brand { display: flex; align-items: center; gap: 1rem; cursor: pointer; }
        .navbar__logo { height: 38px; }
        .navbar__title { font-size: 1.2rem; font-weight: 900; letter-spacing: 0.08em; }
        .navbar__subtitle { font-size: 0.6rem; color: ${C.gold}; letter-spacing: 0.35em; font-weight: 800; }
        .navbar__toggle { display: none; background: none; border: none; cursor: pointer; padding: 0.5rem; }
        .navbar__links { display: flex; align-items: center; gap: 1.5rem; }
        .navbar__link { background: none; border: none; color: white; cursor: pointer; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; transition: color 0.3s; padding: 0.5rem; }
        .navbar__link:hover { color: ${C.gold}; }

        /* ── HERO ── */
        .hero { min-height: 100vh; display: flex; align-items: center; position: relative; background: url('/bg-hero.png') center center no-repeat; background-size: cover; image-rendering: auto; padding-top: 5rem; }
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) { .hero { background-size: cover; } }
        .hero__overlay { position: absolute; inset: 0; background: linear-gradient(100deg, ${C.black} 0%, rgba(5,5,5,0.85) 45%, rgba(5,5,5,0.3) 100%); z-index: 1; }
        .hero__content { max-width: 1400px; margin: 0 auto; width: 100%; padding: 0 1.5rem; display: flex; align-items: center; gap: 3rem; position: relative; z-index: 2; }
        .hero__text { flex: 1.3; }
        .hero__badge { display: inline-flex; align-items: center; gap: 0.8rem; background: rgba(0,255,255,0.06); border: 1px solid ${C.cyan}33; padding: 0.6rem 1.2rem; border-radius: 50px; margin-bottom: 2rem; }
        .hero__badge span { font-size: 0.75rem; font-weight: 900; color: ${C.cyan}; letter-spacing: 0.15em; }
        .hero__h1 { font-size: clamp(2.5rem, 6vw, 5.5rem); font-weight: 950; line-height: 0.95; letter-spacing: -0.02em; margin-bottom: 2rem; }
        .hero__desc { font-size: clamp(1rem, 1.5vw, 1.3rem); color: rgba(255,255,255,0.7); line-height: 1.6; max-width: 600px; margin-bottom: 3rem; }
        .hero__buttons { display: flex; gap: 1.5rem; flex-wrap: wrap; }
        .hero__stats { display: flex; gap: 3rem; margin-top: 4rem; flex-wrap: wrap; }
        .hero__stat { border-left: 2px solid ${C.gold}; padding-left: 1.2rem; }
        .hero__stat-val { color: ${C.gold}; font-weight: 900; font-size: 1.3rem; }
        .hero__stat-label { font-size: 0.7rem; color: rgba(255,255,255,0.5); font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; }
        .hero__image { flex: 0.8; display: flex; justify-content: center; }
        .hero__logo-float { width: 100%; max-width: 450px; animation: float 8s ease-in-out infinite; filter: drop-shadow(0 0 25px ${C.cyan}22); }

        /* ── STATS BAR ── */
        .stats-bar { background: ${C.navy}; padding: 3rem 1.5rem; display: grid; grid-template-columns: repeat(4, 1fr); text-align: center; border-top: 1px solid rgba(255,255,255,0.05); border-bottom: 1px solid rgba(255,255,255,0.05); }
        .stats-bar__num { font-size: clamp(1.8rem, 3vw, 2.8rem); font-weight: 950; color: ${C.cyan}; }
        .stats-bar__label { font-size: 0.75rem; color: rgba(255,255,255,0.5); font-weight: 700; text-transform: uppercase; margin-top: 0.3rem; }

        /* ── SERVICES ── */
        .services { padding: 6rem 0; background: url('/bg-steel.png') center center no-repeat; background-size: cover; background-attachment: scroll; position: relative; }
        .services__overlay { position: absolute; inset: 0; background: rgba(5,5,5,0.93); }
        .services__inner { position: relative; z-index: 2; }
        .services__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
        .service-card { background: rgba(13,27,42,0.7); padding: 3rem 2.5rem; border-radius: 30px; border: 1px solid ${C.gold}22; transition: border-color 0.4s, transform 0.3s; }
        .service-card:hover { border-color: ${C.gold}; transform: translateY(-4px); }
        .service-card__icon { background: ${C.gold}; width: 60px; height: 60px; border-radius: 18px; display: flex; align-items: center; justify-content: center; margin-bottom: 2rem; }
        .service-card__title { font-size: 1.6rem; font-weight: 950; margin-bottom: 1rem; }
        .service-card__desc { color: rgba(255,255,255,0.6); line-height: 1.6; margin-bottom: 2rem; }
        .service-card__list { border-top: 1px solid rgba(255,255,255,0.1); padding-top: 1.5rem; }
        .service-card__item { display: flex; align-items: center; gap: 0.7rem; margin-bottom: 0.7rem; color: rgba(255,255,255,0.8); font-weight: 600; font-size: 0.9rem; }

        /* ── PORTFOLIO ── */
        .portfolio { padding: 6rem 0; background: ${C.black}; }
        .portfolio__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
        .project-card { border-radius: 24px; overflow: hidden; background: ${C.anthracite}; border: 1px solid rgba(255,255,255,0.05); transition: transform 0.3s; }
        .project-card:hover { transform: translateY(-4px); }
        .project-card__img { height: 260px; overflow: hidden; }
        .project-card__img img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s; }
        .project-card:hover .project-card__img img { transform: scale(1.05); }
        .project-card__body { padding: 2rem; }
        .project-card__cat { color: ${C.gold}; font-weight: 900; font-size: 0.7rem; letter-spacing: 0.2em; text-transform: uppercase; margin-bottom: 0.7rem; }
        .project-card__title { font-size: 1.3rem; font-weight: 950; margin-bottom: 0.8rem; }
        .project-card__desc { color: rgba(255,255,255,0.5); font-size: 0.95rem; line-height: 1.5; }

        /* ── PROCESS ── */
        .process { padding: 6rem 0; background: ${C.navy}; }
        .process__inner { display: flex; gap: 5rem; align-items: center; }
        .process__text { flex: 1; }
        .process__steps { display: flex; flex-direction: column; gap: 2.5rem; margin-top: 3rem; }
        .process__step { display: flex; gap: 2rem; }
        .process__step-num { font-size: 2.5rem; font-weight: 950; color: ${C.gold}; opacity: 0.3; line-height: 1; min-width: 50px; }
        .process__step-title { font-size: 1.3rem; font-weight: 950; margin-bottom: 0.5rem; }
        .process__step-desc { color: rgba(255,255,255,0.6); line-height: 1.6; }
        .process__visual { flex: 1; position: relative; }
        .process__img { width: 100%; border-radius: 30px; box-shadow: 0 30px 80px rgba(0,0,0,0.7); }
        .process__badge { position: absolute; bottom: -25px; right: -15px; background: ${C.gold}; padding: 2rem; border-radius: 24px; color: black; text-align: center; }
        .process__badge-title { font-weight: 950; font-size: 1.6rem; }
        .process__badge-sub { font-weight: 800; font-size: 0.7rem; letter-spacing: 0.1em; }

        /* ── DIFFERENTIALS ── */
        .differentials { padding: 6rem 0; background: ${C.cobalt}; }
        .differentials__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
        .diff-card { background: rgba(255,255,255,0.05); padding: 2.5rem 2rem; border-radius: 24px; border: 1px solid rgba(255,255,255,0.08); transition: transform 0.3s; }
        .diff-card:hover { transform: translateY(-3px); }
        .diff-card__icon { width: 55px; height: 55px; background: rgba(0,255,255,0.1); border-radius: 16px; display: flex; align-items: center; justify-content: center; margin-bottom: 1.5rem; }
        .diff-card__title { font-size: 1.2rem; font-weight: 900; margin-bottom: 0.7rem; }
        .diff-card__desc { color: rgba(255,255,255,0.6); line-height: 1.6; font-size: 0.9rem; }

        /* ── TESTIMONIALS ── */
        .testimonials { padding: 6rem 0; background: ${C.black}; }
        .testimonials__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
        .testi-card { background: ${C.anthracite}; padding: 3rem 2.5rem; border-radius: 24px; border: 1px solid rgba(255,255,255,0.05); }
        .testi-card__stars { display: flex; gap: 0.3rem; margin-bottom: 1.5rem; }
        .testi-card__text { font-size: 1rem; color: rgba(255,255,255,0.8); line-height: 1.7; margin-bottom: 2rem; font-style: italic; }
        .testi-card__author { display: flex; align-items: center; gap: 1rem; }
        .testi-card__avatar { width: 50px; height: 50px; background: ${C.gold}; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: black; font-weight: 950; font-size: 1.2rem; flex-shrink: 0; }
        .testi-card__name { font-weight: 900; font-size: 1rem; }
        .testi-card__role { color: ${C.gold}; font-weight: 700; font-size: 0.75rem; }

        /* ── ABOUT ── */
        .about { padding: 6rem 0; background: ${C.navy}; }
        .about__inner { display: flex; gap: 5rem; align-items: flex-start; }
        .about__text { flex: 1; }
        .about__block { display: flex; gap: 1.5rem; margin-top: 2.5rem; padding: 2rem; background: rgba(255,255,255,0.03); border-radius: 20px; border: 1px solid rgba(255,255,255,0.06); }
        .about__block-icon { width: 50px; height: 50px; background: rgba(212,175,55,0.1); border-radius: 14px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .about__block-title { color: ${C.gold}; font-size: 1.4rem; font-weight: 950; margin-bottom: 0.7rem; }
        .about__block-desc { color: rgba(255,255,255,0.7); line-height: 1.7; }
        .about__values { display: grid; grid-template-columns: 1fr 1fr; gap: 0.7rem; }
        .about__value-item { display: flex; align-items: center; gap: 0.6rem; font-weight: 700; font-size: 0.95rem; }
        .about__visual { flex: 1; }
        .about__img-wrapper { position: relative; border-radius: 30px; overflow: hidden; }
        .about__img { width: 100%; display: block; }
        .about__img-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.8) 100%); display: flex; align-items: flex-end; justify-content: center; padding: 2rem; }
        .about__img-logo { height: 80px; filter: drop-shadow(0 0 15px rgba(255,255,255,0.3)); }
        .about__badges { display: flex; gap: 1.5rem; margin-top: 2rem; }
        .about__badge-item { background: ${C.anthracite}; padding: 1.5rem 2rem; border-radius: 20px; flex: 1; text-align: center; border: 1px solid rgba(255,255,255,0.1); }
        .about__badge-val { color: ${C.gold}; font-weight: 950; font-size: 1.3rem; }
        .about__badge-label { font-size: 0.7rem; font-weight: 800; margin-top: 0.3rem; }

        /* ── CONTACT ── */
        .contact { padding: 6rem 0; background: ${C.black}; }
        .contact__inner { display: grid; grid-template-columns: 1fr 1.5fr; gap: 5rem; }
        .contact__title { font-size: clamp(2rem, 4vw, 3.5rem); font-weight: 950; line-height: 1.1; margin-bottom: 2rem; }
        .contact__desc { color: rgba(255,255,255,0.6); font-size: 1.1rem; line-height: 1.7; margin-bottom: 3rem; }
        .contact__channels { display: flex; flex-direction: column; gap: 2rem; }
        .contact__channel { display: flex; align-items: center; gap: 1.5rem; }
        .contact__channel-icon { width: 60px; height: 60px; background: rgba(212,175,55,0.1); border-radius: 18px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .contact__channel-label { color: ${C.gold}; font-weight: 900; font-size: 0.75rem; letter-spacing: 0.15em; }
        .contact__channel-value { font-size: 1.3rem; font-weight: 900; }
        .contact__channel-value--sm { font-size: 1rem; }
        .contact__form { background: ${C.anthracite}; padding: 3rem; border-radius: 30px; border: 1px solid rgba(255,255,255,0.05); }
        .contact__form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 1.5rem; }
        .form-group { margin-bottom: 1rem; }
        .form-group label { display: block; color: ${C.gold}; font-weight: 900; font-size: 0.7rem; letter-spacing: 0.15em; margin-bottom: 0.6rem; }
        .form-group input, .form-group select, .form-group textarea { width: 100%; background: ${C.black}; border: 1px solid #333; padding: 1rem; border-radius: 12px; color: white; font-weight: 700; font-size: 0.9rem; font-family: inherit; transition: border-color 0.3s; }
        .form-group input:focus, .form-group select:focus, .form-group textarea:focus { outline: none; border-color: ${C.gold}; }
        .form-group textarea { resize: none; }
        .form-error { color: #ef4444; font-size: 0.75rem; font-weight: 700; margin-top: 0.4rem; display: block; }
        .input-error { border-color: #ef4444 !important; box-shadow: 0 0 0 2px rgba(239,68,68,0.2); }
        .form-success { display: flex; align-items: center; gap: 0.8rem; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); padding: 1.2rem 1.5rem; border-radius: 16px; margin-bottom: 1.5rem; color: #22c55e; font-weight: 700; }
        .spin { animation: spinner 1s linear infinite; }
        @keyframes spinner { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .btn:disabled { opacity: 0.7; cursor: not-allowed; }

        /* ── FOOTER ── */
        .footer { background: ${C.black}; padding: 5rem 0 3rem; border-top: 1px solid rgba(255,255,255,0.05); }
        .footer__grid { display: grid; grid-template-columns: 2fr 1fr 1.5fr; gap: 4rem; margin-bottom: 4rem; }
        .footer__brand-row { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; }
        .footer__logo { height: 35px; }
        .footer__brand-name { font-weight: 950; font-size: 1.3rem; letter-spacing: 0.08em; }
        .footer__brand-sub { font-size: 0.65rem; color: ${C.gold}; letter-spacing: 0.3em; font-weight: 800; }
        .footer__brand-desc { color: rgba(255,255,255,0.4); line-height: 1.7; font-size: 0.9rem; }
        .footer__col-title { color: ${C.gold}; font-weight: 950; letter-spacing: 0.15em; font-size: 0.85rem; margin-bottom: 1.5rem; }
        .footer__link { display: block; color: rgba(255,255,255,0.5); text-decoration: none; margin-bottom: 0.8rem; font-weight: 600; font-size: 0.9rem; transition: color 0.3s; }
        .footer__link:hover { color: ${C.gold}; }
        .footer__bottom { border-top: 1px solid rgba(255,255,255,0.1); padding-top: 2rem; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem; }
        .footer__copy { color: rgba(255,255,255,0.3); font-size: 0.8rem; font-weight: 600; }
        .footer__credits { display: flex; align-items: center; gap: 0.5rem; color: rgba(255,255,255,0.5); font-weight: 700; font-size: 0.85rem; }
        .footer__credits a { color: ${C.gold}; text-decoration: none; }
        .footer__legal { display: flex; gap: 2rem; }
        .footer__legal a { color: rgba(255,255,255,0.3); text-decoration: none; font-size: 0.8rem; font-weight: 600; }
        .footer__legal a:hover { color: ${C.gold}; }

        /* ── ANIMATIONS ── */
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }

        /* ═══════════════════════════════════════════════
           RESPONSIVE — TABLET (≤1024px)
        ═══════════════════════════════════════════════ */
        @media (max-width: 1024px) {
          .hero__image { display: none; }
          .hero__h1 { font-size: clamp(2.5rem, 7vw, 4rem); }
          .services__grid { grid-template-columns: 1fr; }
          .portfolio__grid { grid-template-columns: repeat(2, 1fr); }
          .process__inner { flex-direction: column; }
          .process__visual { width: 100%; }
          .process__badge { position: static; margin-top: 1.5rem; display: inline-block; }
          .differentials__grid { grid-template-columns: repeat(2, 1fr); }
          .testimonials__grid { grid-template-columns: 1fr; }
          .about__inner { flex-direction: column; }
          .about__visual { width: 100%; }
          .contact__inner { grid-template-columns: 1fr; }
          .footer__grid { grid-template-columns: 1fr 1fr; gap: 2rem; }
        }

        /* ═══════════════════════════════════════════════
           RESPONSIVE — MOBILE (≤768px)
        ═══════════════════════════════════════════════ */
        @media (max-width: 768px) {
          .topbar { font-size: 0.6rem; letter-spacing: 0.1em; padding: 0.4rem 0.5rem; }
          .navbar { top: 2rem; width: 96%; border-radius: 20px; padding: 0.5rem 1rem; flex-wrap: wrap; }
          .navbar__logo { height: 30px; }
          .navbar__title { font-size: 1rem; }
          .navbar__subtitle { font-size: 0.5rem; }
          .navbar__toggle { display: block; }
          .navbar__links { display: none; width: 100%; flex-direction: column; padding: 1rem 0; gap: 0.5rem; }
          .navbar__links--open { display: flex; }
          .navbar__link { padding: 0.8rem; width: 100%; text-align: center; border-radius: 12px; }
          .navbar__link:hover { background: rgba(255,255,255,0.05); }

          .hero { padding-top: 8rem; min-height: auto; padding-bottom: 4rem; }
          .hero__badge { display: none; }
          .hero__h1 { font-size: 2.2rem; }
          .hero__desc { font-size: 0.95rem; }
          .hero__buttons { flex-direction: column; }
          .hero__buttons .btn { width: 100%; justify-content: center; }
          .hero__stats { gap: 1.5rem; }

          .stats-bar { grid-template-columns: repeat(2, 1fr); gap: 1.5rem; padding: 2rem 1rem; }

          .services, .portfolio, .process, .differentials, .testimonials, .about, .contact { padding: 4rem 0; }
          .section-header { margin-bottom: 2.5rem; }
          .section-header__title { font-size: 1.8rem; }

          .portfolio__grid { grid-template-columns: 1fr; }
          .project-card__img { height: 200px; }

          .differentials__grid { grid-template-columns: 1fr; }

          .about__block { flex-direction: column; gap: 1rem; padding: 1.5rem; }
          .about__values { grid-template-columns: 1fr; }
          .about__badges { flex-direction: column; }

          .contact__form { padding: 2rem; }
          .contact__form-row { grid-template-columns: 1fr; }
          .contact__channel-value { font-size: 1rem; }

          .footer__grid { grid-template-columns: 1fr; gap: 2rem; }
          .footer__bottom { flex-direction: column; text-align: center; }
          .footer__legal { justify-content: center; }
        }

        /* ═══════════════════════════════════════════════
           RESPONSIVE — SMALL MOBILE (≤480px)
        ═══════════════════════════════════════════════ */
        @media (max-width: 480px) {
          .hero__h1 { font-size: 1.8rem; }
          .hero__stats { flex-direction: column; gap: 1rem; }
          .stats-bar__num { font-size: 1.5rem; }
          .service-card { padding: 2rem 1.5rem; }
          .testi-card { padding: 2rem 1.5rem; }
        }

        /* ── SCROLL REVEAL ANIMATIONS ── */
        .reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .revealed {
          opacity: 1;
          transform: translateY(0);
        }
        .revealed .service-card,
        .revealed .project-card,
        .revealed .diff-card,
        .revealed .testi-card,
        .revealed .about__block {
          opacity: 0;
          animation: fadeInUp 0.6s ease forwards;
        }
        .revealed .service-card:nth-child(1), .revealed .project-card:nth-child(1), .revealed .diff-card:nth-child(1), .revealed .testi-card:nth-child(1), .revealed .about__block:nth-child(1) { animation-delay: 0s; }
        .revealed .service-card:nth-child(2), .revealed .project-card:nth-child(2), .revealed .diff-card:nth-child(2), .revealed .testi-card:nth-child(2), .revealed .about__block:nth-child(2) { animation-delay: 0.1s; }
        .revealed .service-card:nth-child(3), .revealed .project-card:nth-child(3), .revealed .diff-card:nth-child(3), .revealed .testi-card:nth-child(3), .revealed .about__block:nth-child(3) { animation-delay: 0.2s; }
        .revealed .project-card:nth-child(4), .revealed .diff-card:nth-child(4) { animation-delay: 0.3s; }
        .revealed .project-card:nth-child(5), .revealed .diff-card:nth-child(5) { animation-delay: 0.4s; }
        .revealed .project-card:nth-child(6), .revealed .diff-card:nth-child(6) { animation-delay: 0.5s; }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ── REDUCED MOTION ── */
        @media (prefers-reduced-motion: reduce) {
          .reveal { opacity: 1; transform: none; transition: none; }
          .revealed .service-card, .revealed .project-card, .revealed .diff-card, .revealed .testi-card, .revealed .about__block { animation: none; opacity: 1; }
          .hero__logo-float { animation: none; }
        }
      `}</style>
    </div>
  );
}

export default App;
